import React from "react";
import { useAppDispatch, useAppSelector, useAppTranslation } from "@/utils";
import {
  Button,
  Col,
  Descriptions,
  Divider,
  Drawer,
  Image,
  Progress,
  Row,
  Space,
  Typography,
  useMessagedAsyncFn,
} from "@payping/web-components";
import { useNavigate } from "react-router-dom";
import { Add, Close, FormOutlined } from "@payping/icons";
import { useAsyncRetry } from "react-use";
import { getUserBusinessinfo, getUserLocalInformation, updateBusinessinfo } from "@payping/redux/actions";
import { dateFormats, toJalali, toUtc } from "@payping/utils/dateUtils";
import { DashboardRoutes } from "@/router/constant";
import { userApis } from "@payping/api";
import location from "@/assets/location.json";
import { isBusinessRegisterURL } from "@/utils/isRegisterURL";
import { selectAtom } from "jotai/utils";
import { settingsBusinessAtom } from "../state";
import { useAtom } from "jotai";

const mobileFormEditAtom = selectAtom(settingsBusinessAtom, (val) => val.mobileFormEdit);

const MobileBusinessList: React.FC = () => {
  const { t } = useAppTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const businessInfo = useAppSelector((state) => state.user.businessInfo);
  const localInfo = useAppSelector((state) => state.user.localInfo);
  const businessDocuments = useAppSelector((state) => state.user.businessInfo?.documents);
  const [mobileFormEditState] = useAtom(mobileFormEditAtom);

  const isRegisterURL = isBusinessRegisterURL();

  useAsyncRetry(async () => {
    await dispatch(getUserLocalInformation());
    await dispatch(getUserBusinessinfo());
  }, [dispatch]);

  const [{ loading: loadingSubmit }, onSubmit] = useMessagedAsyncFn(async () => {
    const finalBusinessData = {
      businessDescription: mobileFormEditState?.businessDescription ?? businessInfo?.businessDescription,
      city: mobileFormEditState?.city ?? localInfo?.city,
      address: mobileFormEditState?.address ?? localInfo?.address,
      companyName: mobileFormEditState?.companyName ?? businessInfo?.companyName,
      site: mobileFormEditState?.site ?? businessInfo?.site,
      taxCode: mobileFormEditState?.taxCode ?? businessInfo?.taxCode,
      postalCode: mobileFormEditState?.postalCode ?? localInfo?.postalCode,
      localPhone: mobileFormEditState?.localPhone ?? localInfo?.localPhone,
      // Officials
      isOfficialSubmited: mobileFormEditState?.isOfficialSubmited ?? businessInfo?.isOfficialSubmited,
      sabtNumber: mobileFormEditState?.sabtNumber ?? businessInfo?.sabtNumber,
      businessName: mobileFormEditState?.businessName ?? businessInfo?.businessName,
      bNationalCode: mobileFormEditState?.bNationalCode ?? businessInfo?.bNationalCode,
      registrationDate: mobileFormEditState?.registrationDate ?? businessInfo?.registrationDate,
      economicCode: mobileFormEditState?.economicCode ?? businessInfo?.economicCode,
    };
    await dispatch(
      updateBusinessinfo({
        registrationDate: toUtc({
          convertTime: false,
          date: finalBusinessData.registrationDate,
          toFormat: dateFormats.standard.utcDate,
        }).string,

        businessDescription: finalBusinessData.businessDescription,
        businessName: finalBusinessData.businessName,
        economicCode: finalBusinessData.economicCode,
        isOfficialSubmited: finalBusinessData.isOfficialSubmited,
        sabtNumber: finalBusinessData.sabtNumber,
        site: finalBusinessData.site,
        taxCode: finalBusinessData.taxCode,
        bNationalCode: finalBusinessData.bNationalCode,
        companyName: finalBusinessData.companyName,
      }),
    );
    if (mobileFormEditState?.state && mobileFormEditState.city) {
      const stateData = location.filter((state) => state.stateName === mobileFormEditState?.state);
      const { cities } = stateData[0];
      const cityData = cities.filter((c) => c.cityName === mobileFormEditState?.city);
      await userApis.localInformationsWithRequestModelWithCancellationTokenPut({
        cityId: cityData[0].cityId,
        localPhoneNumber: finalBusinessData.localPhone,
        postalCode: finalBusinessData.postalCode,
        address: finalBusinessData.address,
      });
    }
    if (isRegisterURL) {
      navigate(DashboardRoutes.register.bank.url);
    } else {
      navigate(DashboardRoutes.settings.mRoot.url);
    }
  }, [businessDocuments]);

  function stripFirstSegment(url: string) {
    const parts = url.split("/").filter(Boolean);
    if (parts.length > 1) {
      return parts.slice(1).join("/");
    }
    return "no / included";
  }

  function mobileBusinessNavigation(fullUrl: string) {
    const subUrl = stripFirstSegment(fullUrl);
    if (isRegisterURL) return navigate(`/register/${subUrl}`);
    return navigate(`/settings/${subUrl}`);
  }

  return (
    <Drawer
      title={
        isRegisterURL
          ? t("settings.business.paymentGatewayRequestForm")
          : businessInfo?.businessName || t("businessName")
      }
      open
      height={"100%"}
      placement={"bottom"}
      closeIcon={false}
      extra={
        <Button
          type={"text"}
          icon={<Close width={14} height={14} />}
          onClick={() => {
            navigate(DashboardRoutes.settings.mRoot.url);
          }}
        />
      }
      footer={
        <Row gutter={[8, 8]}>
          <Col flex={3}>
            <Button className={"tw-w-full"} loading={loadingSubmit} type={"primary"} onClick={onSubmit}>
              {isRegisterURL ? t("saveAndNextLevel") : t("save")}
            </Button>
          </Col>
          <Col span={12}>
            <Button className={"tw-w-full"}>{t("close")}</Button>
          </Col>
        </Row>
      }
    >
      <Row>
        <Col xs={24}>
          <Descriptions
            column={1}
            layout={"vertical"}
            size={"large"}
            title={
              <Space direction={"vertical"} className={"tw-w-full"} size={"middle"}>
                <Typography.Title className={"!tw-m-0 tw-font-medium"} level={4}>
                  {t("settings.business.businessInformation")}
                </Typography.Title>
                {isRegisterURL ? (
                  <Progress steps={3} percent={60} format={(percent) => `${(percent || 30) / 30} از 3`} />
                ) : null}
              </Space>
            }
          >
            <Descriptions.Item label={t("businessName")}>
              <Space
                onClick={() => mobileBusinessNavigation(DashboardRoutes.settings.mEditBusiness.name.url)}
                size={"small"}
                direction={"vertical"}
                className={"tw-w-full"}
              >
                <Typography.Text>
                  {(mobileFormEditState?.businessName ?? businessInfo?.businessName) || t("notRegistered")}
                </Typography.Text>
                <Divider className={"background--gray100 !tw-m-0"} />
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label={t("settings.business.description")}>
              <Space
                onClick={() => mobileBusinessNavigation(DashboardRoutes.settings.mEditBusiness.description.url)}
                size={"small"}
                direction={"vertical"}
                className={"tw-w-full"}
              >
                <Typography.Text>
                  {(mobileFormEditState?.businessDescription ?? businessInfo?.businessDescription) ||
                    t("notRegistered")}
                </Typography.Text>
                <Divider className={"background--gray100 !tw-m-0"} />
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label={t("state")}>
              <Space
                onClick={() => mobileBusinessNavigation(DashboardRoutes.settings.mEditBusiness.state.url)}
                size={"small"}
                direction={"vertical"}
                className={"tw-w-full"}
              >
                <Typography.Text>
                  {(mobileFormEditState?.state ?? localInfo?.state) || t("notRegistered")}
                </Typography.Text>
                <Divider className={"background--gray100 !tw-m-0"} />
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label={t("city")}>
              <Space
                onClick={() => {
                  if (mobileFormEditState?.state || localInfo?.state)
                    mobileBusinessNavigation(DashboardRoutes.settings.mEditBusiness.city.url);
                }}
                size={"small"}
                direction={"vertical"}
                className={"tw-w-full"}
              >
                {mobileFormEditState?.state || localInfo?.state ? (
                  <Typography.Text>
                    {(mobileFormEditState?.city ??
                      (mobileFormEditState?.state && mobileFormEditState?.state === localInfo?.state
                        ? localInfo?.city
                        : undefined)) ||
                      t("notRegistered")}
                  </Typography.Text>
                ) : (
                  <Typography.Text>{t("settings.business.wantToSelectCity")}</Typography.Text>
                )}
                <Divider className={"background--gray100 !tw-m-0"} />
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label={t("settings.business.postalAddress")}>
              <Space
                onClick={() => mobileBusinessNavigation(DashboardRoutes.settings.mEditBusiness.address.url)}
                size={"small"}
                direction={"vertical"}
                className={"tw-w-full"}
              >
                <Typography.Text>
                  {(mobileFormEditState?.address ?? localInfo?.address) || t("notRegistered")}
                </Typography.Text>
                <Divider className={"background--gray100 !tw-m-0"} />
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label={t("localPhone")}>
              <Space
                onClick={() => mobileBusinessNavigation(DashboardRoutes.settings.mEditBusiness.phone.url)}
                size={"small"}
                direction={"vertical"}
                className={"tw-w-full"}
              >
                <Typography.Text>
                  {(mobileFormEditState?.localPhone ?? localInfo?.localPhone) || t("notRegistered")}
                </Typography.Text>
                <Divider className={"background--gray100 !tw-m-0"} />
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label={t("postalCode")}>
              <Space
                onClick={() => mobileBusinessNavigation(DashboardRoutes.settings.mEditBusiness.postalCode.url)}
                size={"small"}
                direction={"vertical"}
                className={"tw-w-full"}
              >
                <Typography.Text>
                  {(mobileFormEditState?.postalCode ?? localInfo?.postalCode) || t("notRegistered")}
                </Typography.Text>
                <Divider className={"background--gray100 !tw-m-0"} />
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label={t("settings.business.websiteAddress")}>
              <Space
                onClick={() => mobileBusinessNavigation(DashboardRoutes.settings.mEditBusiness.site.url)}
                size={"small"}
                direction={"vertical"}
                className={"tw-w-full"}
              >
                <Typography.Text>
                  {(mobileFormEditState?.site ?? businessInfo?.site) || t("notRegistered")}
                </Typography.Text>
                <Divider className={"background--gray100 !tw-m-0"} />
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label={t("taxCode")}>
              <Space
                onClick={() => mobileBusinessNavigation(DashboardRoutes.settings.mEditBusiness.taxCode.url)}
                size={"small"}
                direction={"vertical"}
                className={"tw-w-full"}
              >
                <Typography.Text>
                  {(mobileFormEditState?.taxCode ?? businessInfo?.taxCode) || t("notRegistered")}
                </Typography.Text>
                <Divider className={"background--gray100 !tw-m-0"} />
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label={t("settings.business.businessRegistrationStatus")}>
              <Space
                onClick={() =>
                  mobileBusinessNavigation(DashboardRoutes.settings.mEditBusiness.businessRegistrationStatus.url)
                }
                size={"small"}
                direction={"vertical"}
                className={"tw-w-full"}
              >
                <Typography.Text>
                  {(mobileFormEditState?.isOfficialSubmited ?? businessInfo?.isOfficialSubmited)
                    ? t("settings.business.officialRegisteredCompany")
                    : t("settings.business.unofficial")}
                </Typography.Text>
                <Divider className={"background--gray100 !tw-m-0"} />
              </Space>
            </Descriptions.Item>
          </Descriptions>
        </Col>
        {mobileFormEditState.isOfficialSubmited ? (
          <>
            <Col xs={24}>
              <Descriptions column={1} layout={"vertical"} size={"large"}>
                <Descriptions.Item label={t("settings.business.officialBusinessName")}>
                  <Space
                    onClick={() =>
                      mobileBusinessNavigation(DashboardRoutes.settings.mEditBusiness.officialBusinessName.url)
                    }
                    size={"small"}
                    direction={"vertical"}
                    className={"tw-w-full"}
                  >
                    <Typography.Text>
                      {(mobileFormEditState?.companyName ?? businessInfo?.companyName) || t("notRegistered")}
                    </Typography.Text>
                    <Divider className={"background--gray100 !tw-m-0"} />
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label={t("settings.business.sabtNumber")}>
                  <Space
                    onClick={() =>
                      mobileBusinessNavigation(DashboardRoutes.settings.mEditBusiness.officialBusinessSabtNumber.url)
                    }
                    size={"small"}
                    direction={"vertical"}
                    className={"tw-w-full"}
                  >
                    <Typography.Text>
                      {(mobileFormEditState?.sabtNumber ?? businessInfo?.sabtNumber) || t("notRegistered")}
                    </Typography.Text>
                    <Divider className={"background--gray100 !tw-m-0"} />
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label={t("settings.business.registrationDate")}>
                  <Space
                    onClick={() =>
                      mobileBusinessNavigation(
                        DashboardRoutes.settings.mEditBusiness.officialBusinessRegistrationDate.url,
                      )
                    }
                    size={"small"}
                    direction={"vertical"}
                    className={"tw-w-full"}
                  >
                    <Typography.Text>
                      {toJalali({
                        date: mobileFormEditState?.registrationDate ?? businessInfo?.registrationDate,
                        dateFormat: dateFormats.ui.utcDateTime,
                        toFormat: dateFormats.ui.utcDate,
                      }).string || t("notRegistered")}
                    </Typography.Text>
                    <Divider className={"background--gray100 !tw-m-0"} />
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label={t("settings.business.bNationalCode")}>
                  <Space
                    onClick={() =>
                      mobileBusinessNavigation(DashboardRoutes.settings.mEditBusiness.officialBusinessbNationalCode.url)
                    }
                    size={"small"}
                    direction={"vertical"}
                    className={"tw-w-full"}
                  >
                    <Typography.Text>
                      {(mobileFormEditState?.bNationalCode ?? businessInfo?.bNationalCode) || t("notRegistered")}
                    </Typography.Text>
                    <Divider className={"background--gray100 !tw-m-0"} />
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label={t("settings.business.economicCode")}>
                  <Space
                    onClick={() =>
                      mobileBusinessNavigation(DashboardRoutes.settings.mEditBusiness.officialBusinessEconomicCode.url)
                    }
                    size={"small"}
                    direction={"vertical"}
                    className={"tw-w-full"}
                  >
                    <Typography.Text>
                      {(mobileFormEditState?.economicCode ?? businessInfo?.economicCode) || t("notRegistered")}
                    </Typography.Text>
                    <Divider className={"background--gray100 !tw-m-0"} />
                  </Space>
                </Descriptions.Item>
              </Descriptions>
            </Col>
            <Col xs={24} className={"mb-3"}>
              {businessDocuments ? (
                <Space direction={"vertical"} className={"tw-w-full"} size={"middle"}>
                  <div className={"tw-flex tw-justify-between"}>
                    <Typography.Text type={"secondary"}>
                      {t("settings.business.certificateOfEstablishmentAndLetterOfIntroduction")}
                    </Typography.Text>
                    <FormOutlined
                      onClick={() =>
                        mobileBusinessNavigation(DashboardRoutes.settings.mEditBusiness.officialBusinessDocument.url)
                      }
                      width={14}
                    />
                  </div>
                  <Space>
                    {businessDocuments?.map((img, i) => <Image key={i} width={100} src={img.serverName} />)}
                  </Space>
                </Space>
              ) : (
                <Space direction={"vertical"} className={"tw-w-full"} size={"middle"}>
                  <Typography.Text type={"secondary"}>
                    {t("settings.business.certificateOfEstablishmentAndLetterOfIntroduction")}
                  </Typography.Text>
                  <Button
                    icon={<Add width={14} height={14} />}
                    onClick={() =>
                      mobileBusinessNavigation(DashboardRoutes.settings.mEditBusiness.officialBusinessDocument.url)
                    }
                  >
                    {t("add.add")}
                  </Button>
                </Space>
              )}
            </Col>
          </>
        ) : (
          <Col xs={24}>
            <Space direction={"vertical"} className={"tw-w-full"} size={"middle"}>
              <div className={"tw-flex tw-justify-between"}>
                <Typography.Text type={"secondary"}>
                  {t("settings.business.certificateOfEstablishmentAndLetterOfIntroduction")}
                </Typography.Text>
                <FormOutlined
                  onClick={() =>
                    mobileBusinessNavigation(DashboardRoutes.settings.mEditBusiness.officialBusinessDocument.url)
                  }
                  width={14}
                />
              </div>
              <Space>{businessDocuments?.map((img, i) => <Image key={i} width={100} src={img.serverName} />)}</Space>
            </Space>
          </Col>
        )}
      </Row>
    </Drawer>
  );
};

export default MobileBusinessList;
